import AppPaths from '../../AppPaths';
import { getDomainConfig } from '../../lib/domainConfig';

/**
 * String growthbook feature read on `/products?pdp=`. `plp` renders the listing, `pdp` opens the shop
 * narrowed to the named trackers, `site` sends the visitor to the tracker's marketing-site page.
 * One experiment rule per ad cell, targeted on the `url` attribute.
 */
export const LANDING_ARM_FLAG = 'ecom-landing-arm';
export const DEFAULT_LANDING_ARM = 'plp';

const DEVICES = ['mini', 's3', 'ultra', 'cat'];

// the marketing site's story page per tracker; a multi-tracker list has none and gets the homepage
const SITE_PAGES: Record<string, string> = { mini: '/mini/dog', cat: '/mini/cat', s3: '/collar', ultra: '/ultra' };

/** The trackers named by `pdp=` (comma or hyphen separated), unknown keys dropped. Empty when the param is absent. */
export function parsePdpParam(search: string) {
  const raw = new URLSearchParams(search).get('pdp') ?? '';
  return raw
    .toLowerCase()
    .split(/[,-]/)
    .map((key) => key.trim())
    .filter((key, i, all) => DEVICES.includes(key) && all.indexOf(key) === i);
}

/** The ad's own params (utm, click ids, coupons, `type=cat`) as sent, with only `pdp=` dropped. */
function adParams(search: string) {
  return search
    .replace(/^\?/, '')
    .split('&')
    .filter((pair) => pair && !pair.startsWith('pdp='));
}

function isCatsAd(devices: string[], params: string[]) {
  return devices[0] === 'cat' || params.includes('type=cat');
}

/**
 * The pdp arm: the shop narrowed to the named trackers with the first one open. `cat` is the mini
 * with the cats lineup. `lp=pdp` marks the session as this arm for the dashboard.
 */
export function resolvePdpDestination(devices: string[], search: string) {
  const params = adParams(search);
  const shop = devices.map((key) => (key === 'cat' ? 'mini' : key)).filter((key, i, all) => all.indexOf(key) === i);
  const own = [`products=${shop.join(',')}`, `selected=${shop[0]}`];
  if (isCatsAd(devices, params) && !params.includes('type=cat')) {
    own.push('type=cat');
  }
  return { pathname: AppPaths.Shop, search: `?${[...own, ...params, 'lp=pdp'].join('&')}` };
}

/** The site arm: the tracker's marketing page, ad params forwarded so utm and fbclid survive the hop. */
export function resolveSiteDestination(devices: string[], search: string) {
  const params = adParams(search);
  const key = devices.length === 1 ? devices[0] : '';
  const page = key === 'mini' && isCatsAd(devices, params) ? SITE_PAGES.cat : (SITE_PAGES[key] ?? '/');
  return `https://${getDomainConfig().baseDomain}${page}?${[...params, 'lp=site'].join('&')}`;
}
